import { Question } from 'src/types/helios/Question';
import { Tag, ValuesMap } from 'src/types/helios/Tags';

/**
 * Extracts unique tag groups and their values from the questions in an audit.
 * This provides audit-scoped tags (only tags actually assigned to questions)
 * instead of all division-level tags, reducing clutter for auditors.
 *
 * @param questions - Array of audit questions
 * @returns Array of Tag objects constructed from question tagsIncluded data
 */
export const extractTagsFromQuestions = (questions: Question[]): Tag[] => {
  const tagGroupMap = new Map<string, Set<string>>();

  questions.forEach((q) => {
    const tags = q.tagsIncluded;
    if (!tags) return;
    Object.entries(tags).forEach(([groupName, values]) => {
      if (!tagGroupMap.has(groupName)) {
        tagGroupMap.set(groupName, new Set());
      }
      values.forEach((v) => tagGroupMap.get(groupName)!.add(v));
    });
  });

  return Array.from(tagGroupMap.entries()).map(([tagGroupName, valuesSet]) => {
    const valuesMap: ValuesMap = {};
    valuesSet.forEach((value) => {
      valuesMap[value] = { description: value, isActive: true };
    });
    return {
      tagId: tagGroupName,
      tagGroupName,
      businessUnit: '',
      description: tagGroupName,
      values: valuesMap,
      isActive: true,
      isMandatoryForQuestions: false,
      isMandatoryForAuditTemplates: false,
      isApplicableForAuditTemplate: false,
      createdAt: '',
      createdBy: '',
    };
  });
};

/**
 * Groups questions by a selected tag group's values.
 * Questions with multiple tag values appear under each relevant group.
 * Questions without the selected tag group appear in "Ungrouped".
 *
 * @param questions - Array of audit questions
 * @param tagGroupName - The tag group name to group by
 * @returns Map of tag value → questions, with "Ungrouped" for untagged questions
 */
export const groupQuestionsByTagValue = (
  questions: Question[],
  tagGroupName: string,
): Map<string, Question[]> => {
  const grouped = new Map<string, Question[]>();
  const ungrouped: Question[] = [];

  // First pass: collect all tag values for this group and initialize buckets
  const allTagValues = new Set<string>();
  questions.forEach((q) => {
    const values = q.tagsIncluded?.[tagGroupName];
    if (values) {
      values.forEach((v) => allTagValues.add(v));
    }
  });

  // Initialize buckets in sorted order
  Array.from(allTagValues)
    .sort()
    .forEach((value) => {
      grouped.set(value, []);
    });

  // Second pass: place questions into buckets
  questions.forEach((q) => {
    const values = q.tagsIncluded?.[tagGroupName];
    if (!values || values.length === 0) {
      ungrouped.push(q);
    } else {
      values.forEach((value) => {
        grouped.get(value)?.push(q);
      });
    }
  });

  // Add ungrouped at the end if any exist
  if (ungrouped.length > 0) {
    grouped.set('Ungrouped', ungrouped);
  }

  return grouped;
};

/**
 * Counts how many questions in an array have been answered (have a non-empty responseResult).
 *
 * @param questions - Array of questions to check
 * @param responseMap - Pre-built map of questionId → response for O(1) lookups
 * @returns Number of questions that have a response result
 */
export const getAnsweredCount = (
  questions: Question[],
  responseMap: ReadonlyMap<string, { responseResult?: string }>,
): number => {
  return questions.filter((q) => {
    const result = responseMap.get(q.questionId)?.responseResult;
    return result && result !== '';
  }).length;
};
